/**
 * Normalizes a query string value by converting plus signs to spaces.
 * URLSearchParams.get() handles + → space, but %2B → + (literal plus).
 * Some URLs use %2B to represent spaces, so we normalize both to spaces.
 */
const normalizeQueryValue = (value: string): string => value.replace(/\+/g, ' ');

/**
 * Safely parses a URL or query string and returns its searchParams, or null if invalid.
 * Handles absolute URLs, relative URLs, and raw query strings.
 */
const safeParseUrl = (urlOrParams: string): URLSearchParams | null => {
    if (!urlOrParams) {
        return null;
    }

    try {
        if (urlOrParams.startsWith('http') || urlOrParams.startsWith('/')) {
            return new URL(urlOrParams, 'https://www.indeed.com').searchParams;
        }
        return new URLSearchParams(urlOrParams);
    } catch {
        return null;
    }
};

/**
 * Extracts the search query from a URL using multiple methods
 */
export const extractQueryFromUrl = (url: string): string => {
    if (!url) {
        return '';
    }

    const urlQueryParams = safeParseUrl(url);
    if (!urlQueryParams) {
        return '';
    }

    // Method 1: Extract from 'mclk=' parameter (encoded click tracking URL)
    const mclk = urlQueryParams.get('mclk');
    if (mclk) {
        const mclkParams = safeParseUrl(mclk);
        const whatInMclk = mclkParams?.get('what');
        if (whatInMclk) {
            return normalizeQueryValue(whatInMclk);
        }
    }

    // Method 2: Extract from 'what=' parameter directly
    const what = urlQueryParams.get('what');
    if (what) {
        return what;
    }

    // Method 3: Extract from 'prevUrl=' parameter (search URL)
    const prevUrl = urlQueryParams.get('prevUrl');
    if (prevUrl) {
        const prevUrlParams = safeParseUrl(prevUrl);
        const qInPrevUrl = prevUrlParams?.get('q');
        if (qInPrevUrl) {
            return normalizeQueryValue(qInPrevUrl);
        }
    }

    // Method 4: Fallback to direct 'q=' parameter
    const q = urlQueryParams.get('q');
    if (q) {
        return q;
    }

    return '';
};

/**
 * Extracts the location from a URL using multiple methods
 */
export const extractLocationFromUrl = (url: string): string => {
    if (!url) {
        return '';
    }

    const urlQueryParams = safeParseUrl(url);
    if (!urlQueryParams) {
        return '';
    }

    // Method 1: Extract from 'mclk=' parameter
    const mclk = urlQueryParams.get('mclk');
    if (mclk) {
        const mclkParams = safeParseUrl(mclk);
        const whereInMclk = mclkParams?.get('where');
        if (whereInMclk) {
            return normalizeQueryValue(whereInMclk);
        }
    }

    // Method 2: Extract from 'where=' parameter directly
    const where = urlQueryParams.get('where');
    if (where) {
        return where;
    }

    // Method 3: Extract from 'prevUrl=' parameter
    const prevUrl = urlQueryParams.get('prevUrl');
    if (prevUrl) {
        const prevUrlParams = safeParseUrl(prevUrl);
        const lInPrevUrl = prevUrlParams?.get('l');
        if (lInPrevUrl) {
            return normalizeQueryValue(lInPrevUrl);
        }
    }

    // Method 4: Fallback to direct 'l=' parameter
    const l = urlQueryParams.get('l');
    if (l) {
        return l;
    }

    return '';
};
